<template>
    <TransitionRoot as="template" :show="isPurchaseModalOpen">
      <Dialog as="div" auto-reopen="true" class="fixed z-10 inset-0 overflow-y-auto" @close="this.closePurchaseModal()">
        <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
            leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
            <DialogOverlay
              class="fixed inset-0 bg-black bg-opacity-75" />
          </TransitionChild>
          <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
  
          <TransitionChild as="template" enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <div
              class="inline-block align-bottom bg-gray-900 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-6">
              <!-- Close Button -->
              <div v-if="this.slide_index != 4" class="block absolute top-0 right-0 pt-4 pr-4">
                <button type="button" class="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  @click="this.closePurchaseModal()">
                  <span class="sr-only">Close</span>
                  <span class="text-2xl font-bold">×</span>
                </button>
              </div>
              <!-- Slides -->
              <div class="relative px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto py-4 mt-4">
                <!-- Preload images here (dynamically loaded) -->
                <img v-if="SC_IMG" class="hidden" :src="SC_IMG" rel="preload" />
                <img v-if="SC_IMG_LESS_200" class="hidden" :src="SC_IMG_LESS_200" rel="preload" />
                <img v-if="SC_IMG_LESS_1000" class="hidden" :src="SC_IMG_LESS_1000" rel="preload" />
  
                <!-- STEP 1 -->
                <div v-if="this.slide_index == 0"
                  class="dark:text-gray-400 text-gray-600 leading-6 text-md max-w-prose mx-auto">
                  <span
                    class="block text-base text-center dark:text-yellow-300 text-yellow-500 font-semibold tracking-wide uppercase">{{
                      modalTitle }}</span>
                  <h1 class="text-2xl tracking-tight font-extrabold dark:text-gray-300 text-gray-700 md:text-3xl my-6">
                    <span class="block xl:inline">Instructions for Payment via GCash</span>
                  </h1>
                  <p>
                    You will be guided through the steps for purchasing the {{ selectedPurchase }} reviewer. Here's a
                    summary before we proceed:
                  </p>
  
                  <div class="flex items-start my-4 space-x-4 my-8">
                    <div
                      class="mx-auto font-bold flex-shrink-0 flex items-center dark:text-gray-800 justify-center h-12 w-12 rounded-full bg-yellow-400 sm:mx-0 sm:h-10 sm:w-10">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 size-6" fill="none" viewBox="0 0 24 24"
                        stroke-width="1.5" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round"
                          d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 0 1 0 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 0 1 0-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375Z" />
                      </svg>
                    </div>
                    <p>
                      Step 1: Enter and verify your discount coupon code, if you have one.
                    </p>
                  </div>
  
  
                  <div class="flex items-start my-4 space-x-4 my-8">
                    <div
                      class="mx-auto font-bold flex-shrink-0 flex items-center dark:text-gray-800 justify-center h-12 w-12 rounded-full bg-yellow-400 sm:mx-0 sm:h-10 sm:w-10">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round"
                          d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                      </svg>
                    </div>
                    <p>
                      Step 2: Scan our GCash QR Code to make the payment.
                    </p>
                  </div>
  
                  <div class="flex items-start my-4 space-x-4 my-8">
                    <div
                      class="mx-auto flex-shrink-0 flex items-center dark:text-gray-800 justify-center h-12 w-12 rounded-full bg-yellow-400 sm:mx-0 sm:h-10 sm:w-10 mt-2">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round"
                          d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                      </svg>
                    </div>
                    <p>
                      Step 3: Once payment is made, take a <strong>screenshot</strong> of the successful transaction in
                      your GCash app for your copy and input the <strong>Ref. No. of your receipt</strong>. Click the
                      <strong>"Payment Sent!"</strong> button once done.
                    </p>
                  </div>
                  <div class="flex items-start my-4 space-x-4 my-8">
                    <div
                      class="mx-auto flex-shrink-0 flex items-center dark:text-gray-800 justify-center h-12 w-12 rounded-full bg-yellow-400 sm:mx-0 sm:h-10 sm:w-10 mt-2">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round"
                          d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
                      </svg>
                    </div>
                    <p>
                      Step 4: {{ selectedPurchase }} access will be granted after payment is received and confirmed
                      (usually within 5 minutes). Expect an email confirmation within 24 hours after purchase.
                    </p>
                  </div>
                  <!-- <p class="my-8">
                      Click "Continue" below to proceed with the payment.
                    </p> -->
                  <!-- <p class="my-8 text-red-400">
                      <strong class="text-sm mr-2 text-red-900 bg-red-500 px-2 py-0.5 animate-pulse">IMPORTANT</strong>Please don't forget to add your <strong>email address</strong> as "message" when sending the payment in GCash. We need this to verify your payment.
                    </p> -->
                  <button @click="this.nextStep(1)"
                    class="mt-2 text-white bg-indigo-600 w-full py-4 border border-transparent rounded-md shadow-sm px-4 inline-flex justify-center text-xl font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-30 disabled:pointer-events-none disabled:cursor-auto">
                    Continue
                  </button>
                </div>
  
                <!-- STEP 2 -->
                <div v-if="this.slide_index == 1"
                  class="dark:text-gray-400 text-gray-600 leading-6 text-md max-w-prose mx-auto">
  
                  <span
                    class="block text-base text-center dark:text-yellow-300 text-yellow-500 font-semibold tracking-wide uppercase">{{
                      modalTitle }}</span>
                  <h1 class="text-2xl tracking-tight font-extrabold dark:text-gray-300 text-gray-700 md:text-3xl my-6">
                    <span class="dark:text-yellow-200 text-yellow-400">Step 1:</span>
                    <span class="ml-2">Discount Coupon</span>
                  </h1>
                  <div>
                      <p>
                        Do you have a discount coupon? If yes, <strong class="text-yellow-400">click the checkbox below</strong> to enter your code. This may have been provided to you by one of our Endorsers.
                      </p>
  
                  <div class="mt-4 flex items-center">
                    <input id="hasCoupon" type="checkbox" v-model="hasCoupon" @change="onHasCouponChange"
                      class="rounded-md h-6 w-6">
                      <label for="hasCoupon" class="text-lg ml-2">I have a discount coupon code</label>
                    </div>
                  <p v-if="basePrice === 1999"
                    class="mt-2 text-sm bg-yellow-100 border border-yellow-300 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-600 rounded px-3 py-2">
                    For ₱1,999 plans, only <strong>₱200</strong> coupons are accepted. <strong>₱1,000</strong> coupons
                    will be rejected.
                  </p>
  
  
                    <div v-if="hasCoupon" class="mt-4">
                      <p>
                        Please enter your unique 8-digit coupon code in the field below to verify its validity and to claim your discount.
                      </p>
                      
                      <div class="mt-4">
                        <input v-model="couponCode" type="text" name="refno" id="refno"
                          class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-200 border-2 rounded-lg p-4"
                          placeholder="Enter coupon code" />
                      </div>
  
                      <div v-if="isCheckingCoupon" class="flex justify-start items-center space-x-2 mt-2">
                        <svg class="animate-spin h-5 w-5 text-yellow-600" xmlns="http://www.w3.org/2000/svg" fill="none"
                          viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                          </circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8h8a8 8 0 11-16 0z"></path>
                        </svg>
                        <p class=" text-yellow-600 text-sm">Checking for coupon code...</p>
                      </div>
                      <div v-if="couponError" class=" text-red-600 mt-2 flex justify-start items-center gap-1">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                          class="size-6 w-4 h-4">
                          <path fill-rule="evenodd"
                            d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
                            clip-rule="evenodd" />
                        </svg>
  
                        <p>{{ couponError }}</p>
                      </div>
                      <div v-if="isCouponValid" class="mt-4">
                        <p>🏷️ Bundle Price: <strong>₱ {{ selectedPricing.selectedDuration.price.toLocaleString()
                        }}</strong></p>
                        <p>🎫 Coupon Discount: <strong>₱ {{ coupon.data.discountAmount.toLocaleString() }}</strong></p>
                        <p>💎 Discounted Price: <strong class="text-green-500">₱ {{ finalPrice.toLocaleString()
                        }}</strong></p>
                      </div>
                    </div>
                    <p v-else class="mt-4">
                      <strong>🏷️ Current Price:</strong> ₱ {{ finalPrice.toLocaleString() }}
                    </p>
  
                  </div>
  
                  <button v-if="!hasCoupon" @click="this.nextStep(2)"
                    class="mt-8 text-white bg-indigo-600 w-full py-4 border border-transparent rounded-md shadow-sm px-4 inline-flex justify-center text-xl font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-30 disabled:pointer-events-none disabled:cursor-auto">
                    I have no Coupon, Continue
                  </button>
                  <button v-else @click="this.nextStep(2)"
                    class="mt-8 text-white bg-indigo-600 w-full py-4 border border-transparent rounded-md shadow-sm px-4 inline-flex justify-center text-xl font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-30 disabled:pointer-events-none disabled:cursor-auto"
                    :disabled="!isCouponValid">
                    Proceed To Next Step
                  </button>
                  <div
                    class="mt-4 text-center w-full text-gray-700 hover:text-gray-500 dark:text-gray-200 dark:hover:text-gray-300 mx-auto text-sm font-medium">
                    <a href="#" @click="this.nextStep(0)">Go back</a>
                  </div>
  
                </div>
  
                <!-- STEP 3 -->
                <div v-if="this.slide_index == 2"
                  class="dark:text-gray-400 text-gray-600 leading-6 text-md max-w-prose mx-auto">
                  <span
                    class="block text-base text-center dark:text-yellow-300 text-yellow-500 font-semibold tracking-wide uppercase">{{
                      modalTitle }}</span>
                  <h1 class="text-2xl tracking-tight font-extrabold dark:text-gray-300 text-gray-700 md:text-3xl my-6">
                    <span class="dark:text-yellow-200 text-yellow-400">Step 2:</span>
                    <span class=""> Scan the QR Code</span>
                  </h1>
                  <div
                    :class="[(isFetchingQRCodes) ? 'animate-pulse' : '', 'rounded-xl bg-true-gray-100 dark:bg-gray-300 h-80 my-8']">
  
                    <div v-if="isFetchingQRCodes"
                      class="mx-auto h-80 bg-white animate-pulse flex justify-center items-center">
                      <p>Fetching QR Code. Please wait.</p>
                    </div>
                    <div v-else>
                      <img v-if="getQRCodeCondition() && coupon.data.discountAmount == 200"
                        class="mx-auto h-80" :src="QR_CODE_IMG_LESS_200" rel="preload" />
                      <img v-else-if="getQRCodeCondition() && coupon.data.discountAmount == 1000"
                        class="mx-auto h-80" :src="QR_CODE_IMG_LESS_1000" rel="preload" />
                      <img v-else class="mx-auto h-80" :src="QR_CODE_IMG" rel="preload" />
                    </div>
  
                  </div>
                  <p>
                    Open your GCash App and scan the QR code above. This should take you to the GCash Express Send page
                    where the payment details are already populated. If there are any issues with the QR Code, you can
                    send the <strong>PHP {{ finalPrice.toLocaleString() }}</strong> payment to GCash number <strong
                      class="text-yellow-500 dark:text-yellow-400"
                      :class="isFetchingQRCodes ? 'animate-pulse opacity-20' : ''">{{ isFetchingQRCodes ? '09XXXXXXXXX' :
                        gcashPhoneNum }}</strong>, or contact your Endorser for further assistance. Click the button below
                    once done.
                  </p>
                  <button @click="this.nextStep(3)"
                    class="mt-8 text-white bg-indigo-600 w-full py-4 border border-transparent rounded-md shadow-sm px-4 inline-flex justify-center text-xl font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-30 disabled:pointer-events-none disabled:cursor-auto">
                    Proceed To Next Step
                  </button>
                  <div
                    class="mt-4 text-center w-full text-gray-700 hover:text-gray-500 dark:text-gray-200 dark:hover:text-gray-300 mx-auto text-sm font-medium">
                    <a href="#" @click="this.nextStep(1)">Go back</a>
                  </div>
                </div>
  
                <!-- STEP 4 -->
                <div v-if="this.slide_index == 3"
                  class="dark:text-gray-400 text-gray-600 leading-6 text-md max-w-prose mx-auto">
                  <span
                    class="block text-base text-center dark:text-yellow-300 text-yellow-500 font-semibold tracking-wide uppercase">{{
                      modalTitle }}</span>
  
                  <div v-if="!didNotPayThruGcash">
  
                    <h1 class="text-2xl tracking-tight font-extrabold dark:text-gray-300 text-gray-700 md:text-3xl my-6">
                      <span class="dark:text-yellow-200 text-yellow-400">Step 3:</span>
                      <span> Enter your Reference Number below</span>
                    </h1>
  
                    <img v-if="coupon && coupon.data.discountAmount == 200"
                      class="-mt-4 mb-8 sm:w-4/6 mx-auto" @load="isLoaded = true" :src="SC_IMG_LESS_200" />
                    <img v-else-if="coupon && coupon.data.discountAmount == 1000"
                      class="-mt-4 mb-8 sm:w-4/6 mx-auto" @load="isLoaded = true" :src="SC_IMG_LESS_1000" />
                    <img v-else class="-mt-4 mb-8 sm:w-4/6 mx-auto" @load="isLoaded = true" :src="SC_IMG" />
  
                    <p>
                      Don't forget to take a <strong class="text-yellow-500 dark:text-yellow-400">screenshot</strong> of
                      the successful transaction in your GCash app for your copy and save it. Input the <strong
                        class="text-yellow-500 dark:text-yellow-400">Ref. No. of your receipt</strong> below (see sample
                      screenshot above). Once done, click the <strong>"Payment Sent!"</strong> button below to complete
                      the payment.
                    </p>
                    <div class="mt-4">
                      <input v-model="refno" type="text" name="refno" id="refno"
                        class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-200 border-2 rounded-lg p-4"
                        placeholder="Enter your 13-digit Reference Number (i.e. Ref. No.) here" />
                    </div>
  
                  </div>
  
                  <div v-else>
  
                    <h1 class="text-2xl tracking-tight font-extrabold dark:text-gray-300 text-gray-700 md:text-3xl my-6">
                      <span class="dark:text-yellow-200 text-yellow-400">Step 4:</span>
                      <span>Upload the proof of payment</span>
                    </h1>
  
                    <img v-if="coupon && coupon.data.discountAmount == 200"
                      class="-mt-4 mb-8 sm:w-4/6 mx-auto" @load="isLoaded = true" :src="SC_PROOF_LESS_200" />
                    <!-- <img v-else-if="coupon && coupon.data.discountAmount == 1000"
                      class="-mt-4 mb-8 sm:w-4/6 mx-auto" @load="isLoaded = true" :src="SC_PROOF_LESS_1000" /> -->
                    <img v-else class="-mt-4 mb-8 sm:w-4/6 mx-auto" @load="isLoaded = true" :src="SC_PROOF" />
  
                    <p>
                      Don't forget to take a <strong class="text-yellow-500 dark:text-yellow-400">screenshot</strong> of
                      your successful payment transaction and save it. Upload the <strong
                        class="text-yellow-500 dark:text-yellow-400">proof of payment</strong> image below (see the sample
                      screenshot above). Once you're done, click the <strong>"Payment Sent!"</strong> button to complete
                      your payment.
                    </p>
  
  
                    <div class="mt-4">
                      <!-- Preview Section -->
                      <div v-if="paymentImagePreview" class="mt-4 flex justify-start items-end p-2 gap-4">
                        <img :src="paymentImagePreview" class=" max-h-24" />
                        <div class="">
                          <p class=" text-sm">{{ paymentImage.name }}</p>
                          <button @click="removeSelectedImage" class="bg-red-600 text-white text-xs px-2 py-1 rounded">
                            Remove
                          </button>
                        </div>
                      </div>
                      <label v-else for="imageUpload"
                        class="cursor-pointer border rounded-md p-2 dark:text-yellow-300 text-yellow-600 dark:border-yellow-300 border-yellow-600 bg-yellow-500 bg-opacity-10 w-full flex flex-col items-center justify-center">
                        <p>Click to upload image</p>
                        <p class="text-xs opacity-80">Make sure to upload a clear copy.</p>
                        <input id="imageUpload" type="file" accept="image/*" @change="onImageSelected" class="hidden" />
                      </label>
                    </div>
  
  
                  </div>
  
                  <div class="flex gap-3 mt-4">
                    <div class="flex h-6 shrink-0 items-center">
                      <div class="group grid size-4 grid-cols-1">
                        <input v-model="didNotPayThruGcash" id="not-use-gcash"
                          aria-describedby="not-use-gcash-description" name="not-use-gcash" type="checkbox"
                          class="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto" />
                        <svg
                          class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                          viewBox="0 0 14 14" fill="none">
                          <path class="opacity-0 group-has-[:checked]:opacity-100" d="M3 8L6 11L11 3.5" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round" />
                          <path class="opacity-0 group-has-[:indeterminate]:opacity-100" d="M3 7H11" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                      </div>
                    </div>
                    <div class="text-sm/6">
                      <label for="not-use-gcash" class="font-medium dark:text-gray-200">I did not use GCash.</label>
                      {{ ' ' }}
                      <span id="not-use-gcash-description" class="text-gray-400 italic">( I used other payment method.
                        )</span>
                    </div>
                  </div>
  
                  <button :disabled="!isRefNoValid || !hasPaymentImage" @click="this.prepareToOnboard()"
                    class="disabled:bg-gray-400 mt-8 text-white bg-indigo-600 w-full py-4 border border-transparent rounded-md shadow-sm px-4 inline-flex justify-center text-xl font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-30 disabled:pointer-events-none disabled:cursor-auto">
                    Payment Sent!
                  </button>
                  <div
                    class="mt-4 text-center w-full text-gray-700 hover:text-gray-500 dark:text-gray-200 dark:hover:text-gray-300 mx-auto text-sm font-medium">
                    <a href="#" @click="this.nextStep(2)">Go back</a>
                  </div>
                </div>
  
                <!-- STEP 5 -->
                <div v-if="this.slide_index == 4"
                  class="dark:text-gray-400 text-gray-600 leading-6 text-md max-w-prose mx-auto">
                  <span
                    class="block text-base text-center dark:text-yellow-300 text-yellow-500 font-semibold tracking-wide uppercase">{{
                      modalTitle }}</span>
                  <h1 class="text-2xl tracking-tight font-extrabold dark:text-gray-300 text-gray-700 md:text-3xl my-6">
                    <span v-if="!readyToExit" class="block xl:inline">One moment please...</span>
                    <span v-else class="block xl:inline">Awesome! <span class="dark:text-yellow-200 text-yellow-400">Thank you</span> for your purchase.</span>
                  </h1>
                  <p>
                    We are excited for you to start your review! Your {{ selectedPurchase }} access will be granted
                    after payment is received and confirmed (usually within 5 minutes). Expect an email confirmation
                    within 24 hours! If you have any concerns, please reach out to us via email at
                    <strong>support@skilltech.ph</strong>.
                  </p>
  
                  <div v-if="!readyToExit" class="relative pt-1 mt-8">
                    <div class="overflow-hidden h-2 mb-4 text-xs flex rounded bg-yellow-200">
                      <div :style="`width: ${progress}%`"
                        class="transition-all duration-500 ease-in-out shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-yellow-500">
                      </div>
                    </div>
                  </div>
  
                  <div class="bg-yellow-50 overflow-hidden rounded-lg text-warm-gray-500 text-left mt-8 max-w-full">
                    <div class="px-4 py-5 sm:py-8 sm:px-9 flex space-x-2 items-start">
                      <div>
                        <span class="text-yellow-400 text-xl">💡</span>
                      </div>
                      <p class="text-yellow-700">
                        <strong>Tip:</strong> {{ tips[current] }}
                      </p>
                    </div>
                  </div>
  
                  <button @click="this.handleExitPurchaseModal()" :disabled="!readyToExit"
                    class="mt-8 text-white bg-indigo-600 w-full py-4 border border-transparent rounded-md shadow-sm px-4 inline-flex justify-center text-lg font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:pointer-events-none disabled:cursor-auto">
                    Proceed to Transactions Page
                  </button>
                </div>
  
              </div>
            </div>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>
  </template>
  
  <script>
  
  // Search for 'switch-gcash-code' and the files that need to be updated will be shown.
  // John gcash QR Code and num
  import JOHN_QR_CODE_IMG from '@/assets/images/qr/QR-5999.png';
  import JOHN_QR_CODE_IMG_LESS_200 from '@/assets/images/qr/QR-5799.png';
  import JOHN_QR_CODE_IMG_LESS_1000 from '@/assets/images/qr/QR-4999.png';
  const JOHN_GCASH_PHONE_NUM = "09062846028";
  
  // Veen gcash QR Code and num
  // import QR_CODE_IMG from '@/assets/images/qr/bogs/QR-5999.png';
  // import QR_CODE_IMG_LESS_200 from '@/assets/images/qr/bogs/QR-5799.png';
  // import QR_CODE_IMG_LESS_1000 from '@/assets/images/qr/bogs/QR-4999.png';
  // const GCASH_PHONE_NUM = "09058562354";
  
  // Mark gcash QR Code and num
  // import QR_CODE_IMG from '@/assets/images/qr/mark/QR-5999.png';
  // import QR_CODE_IMG_LESS_200 from '@/assets/images/qr/mark/QR-5799.png';
  // import QR_CODE_IMG_LESS_1000 from '@/assets/images/qr/mark/QR-4999.png';
  // const GCASH_PHONE_NUM = "09770818567";
  
  
  // import SC_IMG from '@/assets/images/sc/SC-5999.png';
  // import SC_IMG_LESS_200 from '@/assets/images/sc/SC-5799.png';
  // import SC_IMG_LESS_1000 from '@/assets/images/sc/SC-4999.png';
  
  // import SC_PROOF from '@/assets/images/sc/SC-PROOF-5999.png';
  // import SC_PROOF_LESS_200 from '@/assets/images/sc/SC-PROOF-5799.png';
  // import SC_PROOF_LESS_1000 from '@/assets/images/sc/SC-PROOF-4999.png';
  
  // For CAAP Knowledge
  // const REV_PRICE_ORIG = 3999;
  // const REV_PRICE = REV_PRICE_ORIG - 499;
  // const REV_DURATION = 90;
  // const REV_DURATION_WORDS = '3 Months';
  
  // // For CAAP Practical (PLUS)
  // const REV_PLUS_PRICE_ORIG = 2999;
  // const REV_PLUS_PRICE = REV_PLUS_PRICE_ORIG - 500;
  // const REV_PLUS_DURATION = 90;
  // const REV_PLUS_DURATION_WORDS = '3 Months';
  
  // ✅ Dynamically import all possible sample images
  // Webpack will bundle these, but we only use the ones we need at runtime
  const SAMPLE_PAYMENT_IMAGES = {
    '7999': () => import('@/assets/images/sc/SC-7999.png'),
    '7799': () => import('@/assets/images/sc/SC-7799.png'),
    '6999': () => import('@/assets/images/sc/SC-6999.png'), // for eoy sale
    '6799': () => import('@/assets/images/sc/SC-6799.png'), // for eoy sale
    '5999': () => import('@/assets/images/sc/SC-5999.png'),
    '5799': () => import('@/assets/images/sc/SC-5799.png'),
    '4999': () => import('@/assets/images/sc/SC-4999.png'), // for eoy sale
    '4799': () => import('@/assets/images/sc/SC-4799.png'), // for eoy sale
    '3999': () => import('@/assets/images/sc/SC-3999.png'),
    '3799': () => import('@/assets/images/sc/SC-3799.png'),
    '1999': () => import('@/assets/images/sc/SC-1999.png'),
    '1799': () => import('@/assets/images/sc/SC-1799.png'),
  };
  
  const SAMPLE_PROOF_IMAGES = {
    '7999': () => import('@/assets/images/sc/SC-PROOF-7999.png'),
    '7799': () => import('@/assets/images/sc/SC-PROOF-7799.png'),
    '6999': () => import('@/assets/images/sc/SC-PROOF-6999.png'), // for eoy sale
    '6799': () => import('@/assets/images/sc/SC-PROOF-6799.png'), // for eoy sale
    '5999': () => import('@/assets/images/sc/SC-PROOF-5999.png'),
    '5799': () => import('@/assets/images/sc/SC-PROOF-5799.png'),
    '4799': () => import('@/assets/images/sc/SC-PROOF-4799.png'), // for eoy sale
    '4999': () => import('@/assets/images/sc/SC-PROOF-4999.png'), // for eoy sale
    '3999': () => import('@/assets/images/sc/SC-PROOF-3999.png'),
    '3799': () => import('@/assets/images/sc/SC-PROOF-3799.png'),
    '1999': () => import('@/assets/images/sc/SC-PROOF-1999.png'),
    '1799': () => import('@/assets/images/sc/SC-PROOF-1799.png'),
  };
  
  
  import { checkCouponCodeIfExists, checkCouponValidity, useCoupon, convertTimestampToDate } from '@/v3/CouponService'
  import { trackPurchaseCompleted } from '@/utils/analytics'
  
  import moment from "moment";
  import { db, storage, auth } from "@/firebase/config";
  import { doc, getDoc, collection, addDoc, updateDoc, serverTimestamp } from "firebase/firestore";
  import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
  
  import { homeData } from "@/data/home.data";
  
  import {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogOverlay,
    DialogTitle,
  } from "@headlessui/vue";
  
  export default {
    props: ["isPurchaseModalOpen", "user", "selectedPurchase", "selectedPricing"],
    components: {
      TransitionRoot,
      TransitionChild,
      Dialog,
      DialogOverlay,
      DialogTitle,
    },
    data() {
      return {
        // ui data
        modalTitle: "Purchase A Subscription",
        isLoading: false,
        slide_index: 0,
        progress: 0, // progress bar
        readyToExit: false,
        tips: homeData.tips,
        current: 0,
        isLoaded: false,
        refno: "",
        QR_CODE_IMG: null,
        QR_CODE_IMG_LESS_200: null,
        QR_CODE_IMG_LESS_1000: null,
        SC_IMG: null,
        SC_IMG_LESS_200: null,
        SC_IMG_LESS_1000: null,
        SC_PROOF: null,
        SC_PROOF_LESS_200: null,
        SC_PROOF_LESS_1000: null,
        // REV_PRICE,
        // REV_PLUS_PRICE,
        gcashPhoneNum: '',
        activeQrFolder: null,
        couponCode: '',
        coupon: null,
        isCouponValid: false,
        couponError: '',
        isCheckingCoupon: false,
        debounceTimeout: null,
        isFetchingQRCodes: false,
        didNotPayThruGcash: false,
        paymentImage: null,             // File object
        paymentImagePreview: null,      // Base64 or Blob URL for preview
        proofImageURL: null,
        hasCoupon: false,
      isProcessingPayment: false,
      };
    },
    computed: {
      isPurchaseBundleWithCoupon() {
        /**
         * if they purchase full license bundle or full airman bundle.
         */
        return this.selectedPurchase && this.selectedPricing && (this.selectedPricing.key === 'full-license-bundle' || this.selectedPricing.key === 'full-airman-bundle');
      },
    basePrice() {
      if (!this.selectedPricing || !this.selectedPricing.selectedDuration) {
        return 0;
      }
      return this.selectedPricing.selectedDuration.price;
    },
    couponsAllowed() {
      // Coupons are generally allowed, but some plans restrict which discounts are accepted.
      return true;
    },
    hasValidCoupon() {
      return (
        this.couponsAllowed &&
        this.hasCoupon &&
        this.isCouponValid &&
        this.coupon &&
        this.coupon.data &&
        this.coupon.data.discountAmount &&
        // For ₱1,999 plans, only ₱200 coupons are allowed
        (this.basePrice !== 1999 || this.coupon.data.discountAmount === 200)
      );
    },
      isRefNoValid() {
        if (!this.didNotPayThruGcash) {
  
          const ref = this.refno.trim();
          if (ref.length !== 13) return false;
          if (!/^\d{13}$/.test(ref)) return false;
  
          // if(this.refno.length < 13 ) return false;  // invalid if less than 13 chars
          // if(this.refno.length > 13 ) return false;  // invalid if more than 13 chars
          // if(isNaN(this.refno)) return false;        // invalid if not a number
        }
        return true;
      },
      hasPaymentImage() {
        if (this.didNotPayThruGcash && !this.paymentImage) return false;
  
        return true;
      },
      finalPrice() {
        if (!this.selectedPricing || !this.selectedPricing.selectedDuration) {
          return 0;
        }
  
        // If coupon is valid and has discount amount, apply discount
      if (this.hasValidCoupon) {
        return this.basePrice - this.coupon.data.discountAmount;
        }
  
      return this.basePrice;
      }
    },
    watch: {
    'selectedPricing.selectedDuration.price'(newPrice, oldPrice) {
      // If the user changes pricing while the modal is open, enforce coupon rules.
      this.enforceCouponRules();
    },
      // make sure to remove the value of the ref or the proof image once the user toggle away
      didNotPayThruGcash(newVal, oldVal) {
        if (newVal) {
          this.refno = "";
        } else {
          this.paymentImage = null;
          this.paymentImagePreview = null;
        }
      },
      // whenever coupon changes, this function will run
      couponCode(newVal, oldVal) {
        // reset coupon value and isCouponValid
        this.coupon = null;
        this.isCouponValid = false;
        this.couponError = '';
  
        // Clear the previous debounce timeout if it exists
        if (this.debounceTimeout) { clearTimeout(this.debounceTimeout); }
        // Set a new debounce timeout (2 seconds = 2000 ms)
        if (newVal.trim() !== '') {
          this.isCheckingCoupon = true;
          this.debounceTimeout = setTimeout(async () => {
            // Call the function to check if the coupon is valid
  
            await this.checkCouponCode(newVal);
            this.isCheckingCoupon = false;
          }, 2000);
        } else {
          this.isCheckingCoupon = false;
        }
      },
      // Every time the modal opens, determine which QR code to use and load sample images.
      async isPurchaseModalOpen(newVal, oldVal) {
  
        console.log(this.user)
        console.log(this.selectedPurchase)
        console.log(this.selectedPricing)
  
        if (newVal) {
  
          // Reset state
        this.isProcessingPayment = false;
        this.enforceCouponRules();
          this.didNotPayThruGcash = false;
          this.paymentImage = null;             // File object
          this.paymentImagePreview = null;      // Base64 or Blob URL for preview
          this.proofImageURL = null;
  
          // Reset sample images
          this.SC_IMG = null;
          this.SC_IMG_LESS_200 = null;
          this.SC_IMG_LESS_1000 = null;
          this.SC_PROOF = null;
          this.SC_PROOF_LESS_200 = null;
          this.SC_PROOF_LESS_1000 = null;
  
          this.isFetchingQRCodes = true;
          this.activeQrFolder = null;
  
          try {
            const docRef = doc(db, "settings", "qr_codes");
            const docSnap = await getDoc(docRef);
  
            if (docSnap.exists()) {
              const qrData = docSnap.data();
              const activeQrCode = qrData.list.find(qr => qr.isActive) || null; // ✅ Prevent undefined issues
  
              if (!activeQrCode) {
                console.warn("No active QR code found. Using fallback QR codes.");
                this.assignFallbackQR();
                return;
              }

              this.setActiveQrStamp(activeQrCode);
  
              try {
  
                /** identify the price here */
                const basePrice = this.selectedPricing.selectedDuration.price;
  
                // ✅ Fetch QR Code images from Firebase Storage
                const qrCodePaths = [
                  `/images/qrcodes/${activeQrCode.storageFolderName}/QR-${basePrice}.png`,
                  `/images/qrcodes/${activeQrCode.storageFolderName}/QR-${basePrice - 200}.png`,
                  `/images/qrcodes/${activeQrCode.storageFolderName}/QR-${basePrice - 1000}.png`,
                ];
  
                const qrCodeUrls = await Promise.all(
                  qrCodePaths.map(path => getDownloadURL(storageRef(storage, path)).catch(() => null))
                );
  
                // ✅ Preload only non-null images
                const validUrls = qrCodeUrls.filter(url => url !== null);
                await this.preloadImages(validUrls);
  
                // ✅ Assign the preloaded URLs or use fallback images
                this.QR_CODE_IMG = qrCodeUrls[0] || JOHN_QR_CODE_IMG;
                this.QR_CODE_IMG_LESS_200 = qrCodeUrls[1] || JOHN_QR_CODE_IMG_LESS_200;
                this.QR_CODE_IMG_LESS_1000 = qrCodeUrls[2] || JOHN_QR_CODE_IMG_LESS_1000;
  
              } catch (error) {
                console.error("Error fetching QR code images from Firebase Storage:", error);
                this.assignFallbackQR();
              }
  
            } else {
              console.warn("QR code document not found. Using fallback QR codes.");
              this.assignFallbackQR();
            }
  
          } catch (error) {
            console.error("Error retrieving QR code document from Firestore:", error);
            this.assignFallbackQR();
          } finally {
            this.isFetchingQRCodes = false;
          }
  
          // ✅ Dynamically load sample payment images based on selected purchase price
          await this.loadSampleImages();
        }
      }
  
  
    },
    methods: {
    clearCouponState() {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }
      this.coupon = null;
      this.isCouponValid = false;
      this.couponCode = '';
      this.couponError = '';
      this.isCheckingCoupon = false;
    },
    enforceCouponRules() {
      // For ₱1,999 plans, only ₱200 coupons are allowed.
      if (this.basePrice === 1999 && this.hasCoupon) {
        const discount = this.coupon && this.coupon.data ? this.coupon.data.discountAmount : null;
        if (discount && discount !== 200) {
          this.isCouponValid = false;
          this.couponError = "For ₱1,999 plans, only ₱200 coupons are allowed.";
        }
      }
    },
      handleExitPurchaseModal() {
  
        if (!this.readyToExit) {
          return
        } else {
        this.isProcessingPayment = false;
          this.couponCode = '';
          this.$emit("closePurchaseModal");
          this.$router.push({ path: '/v2/dash-v2' });
          setTimeout(() => {
            this.hasCoupon = false;
            this.slide_index = 0;
          }, 500);
        }
      },
      onHasCouponChange() {
        // Clear coupon state when user unchecks the "I have a coupon" checkbox
        if (!this.hasCoupon) {
        this.clearCouponState();
        }
      },
      getQRCodeCondition() {
        // Check all conditions: hasCoupon must be true and coupon must be valid
      return this.hasValidCoupon;
      },
      onImageSelected(event) {
        const file = event.target.files[0];
        if (!file || !file.type.startsWith("image/")) return;
  
        this.paymentImage = file;
        this.paymentImagePreview = URL.createObjectURL(file); // preview
      },
  
      removeSelectedImage() {
        this.paymentImage = null;
        this.paymentImagePreview = null;
      },
      /**
       * ✅ Assigns fallback QR codes when data is missing or errors occur.
       */
      assignFallbackQR() {
        this.QR_CODE_IMG = JOHN_QR_CODE_IMG;
        this.QR_CODE_IMG_LESS_200 = JOHN_QR_CODE_IMG_LESS_200;
        this.QR_CODE_IMG_LESS_1000 = JOHN_QR_CODE_IMG_LESS_1000;
        if (!this.activeQrFolder) {
          this.setActiveQrStamp(null);
        }
      },
      setActiveQrStamp(qr) {
        if (qr && qr.storageFolderName) {
          this.activeQrFolder = qr.storageFolderName;
          this.gcashPhoneNum = qr.gcashPhoneNum || JOHN_GCASH_PHONE_NUM;
        } else {
          this.activeQrFolder = 'john';
          this.gcashPhoneNum = JOHN_GCASH_PHONE_NUM;
        }
      },
      buildTransactionQr() {
        const folder = this.activeQrFolder || 'john';
        const phone = this.gcashPhoneNum || JOHN_GCASH_PHONE_NUM;
        return {
          folder,
          gcash_phone: phone,
          purchased_folder: folder,
          purchased_gcash_phone: phone,
          switched_by: null,
          switched_by_email: null,
          switched_at: null,
        };
      },
      /**
       * ✅ Preload images to ensure they are fully loaded before assigning them.
       */
      async preloadImages(imageUrls) {
        const loadImage = (url) => {
          return new Promise((resolve) => {
            if (!url) return resolve(null); // Ignore null/undefined URLs
            const img = new Image();
            img.src = url;
            img.onload = () => resolve(url); // Resolve when image loads
            img.onerror = () => resolve(null); // Ignore broken images
          });
        };
  
        return Promise.all(imageUrls.map(loadImage));
      },
  
      /**
       * ✅ Dynamically loads sample payment images based on the selected purchase price.
       * Only loads the images that are actually needed at runtime.
       * Webpack will code-split these imports, so they're only loaded when needed.
       */
      async loadSampleImages() {
        if (!this.selectedPricing || !this.selectedPricing.selectedDuration) {
          console.warn("Cannot load sample images: selectedPricing is not available");
          return;
        }
  
        const basePrice = this.selectedPricing.selectedDuration.price;
  
        // Calculate the three possible prices: base, base-200, base-1000
        const prices = [
          basePrice,           // Original price
          basePrice - 200,     // With 200 discount
          basePrice - 1000     // With 1000 discount
        ];
  
        try {
          // Dynamically import only the images we need
          const imagePromises = prices.map(async (price) => {
            const priceKey = price.toString();
  
            // Get the import functions from our mapping
            const paymentImageLoader = SAMPLE_PAYMENT_IMAGES[priceKey];
            const proofImageLoader = SAMPLE_PROOF_IMAGES[priceKey];
  
            if (!paymentImageLoader || !proofImageLoader) {
              console.warn(`No image loaders found for price: ${priceKey}`);
              return { price, paymentImg: null, proofImg: null };
            }
  
            try {
              // Execute the dynamic import functions
              const paymentModule = await paymentImageLoader();
              const proofModule = await proofImageLoader();
  
              return {
                price,
                paymentImg: paymentModule.default || paymentModule,
                proofImg: proofModule.default || proofModule
              };
            } catch (error) {
              console.error(`Error loading images for price ${priceKey}:`, error);
              return { price, paymentImg: null, proofImg: null };
            }
          });
  
          const loadedImages = await Promise.all(imagePromises);
  
          // Assign images based on price
          loadedImages.forEach(({ price, paymentImg, proofImg }) => {
            if (price === basePrice) {
              this.SC_IMG = paymentImg;
              this.SC_PROOF = proofImg;
            } else if (price === basePrice - 200) {
              this.SC_IMG_LESS_200 = paymentImg;
              this.SC_PROOF_LESS_200 = proofImg;
            } 
            // else if (price === basePrice - 1000) {
            //   this.SC_IMG_LESS_1000 = paymentImg;
            //   this.SC_PROOF_LESS_1000 = proofImg;
            // }
          });
  
        } catch (error) {
          console.error("Error loading sample images:", error);
        }
      },
  
      async checkCouponCode(code) {
  
        try {
          console.log(`🎫 Checking if code exists...`)
          this.coupon = await checkCouponCodeIfExists(code)
  
          /**
           * Any error encountered before this line will be caught. '
           * Otherwise, it will proceed normally.
           */
          console.log(`🎫 Coupon code ${code} found. Checking if coupon is valid...`)
          await checkCouponValidity(this.coupon)
  
        // Enforce plan-specific coupon restrictions
        const discountAmount = (this.coupon && this.coupon.data) ? this.coupon.data.discountAmount : null;
        if (this.basePrice === 1999 && discountAmount !== 200) {
          throw "For ₱1,999 plans, only ₱200 coupons are allowed.";
        }

          /**
           * Any error encountered before this line will be caught. 
           * Otherwise, the code is now fully validated. We set the 
           * isCouponValid flag to true.
           */
          console.log(`🎫 Coupon code ${code} is valid. Ready for use.`)
          this.isCouponValid = true
  
        } catch (error) {
          this.couponError = error;
          this.isCouponValid = false;
        }
  
      },
      closePurchaseModal() {
  
        this.couponCode = '';
  
        if (this.slide_index == 4) {
          if (!this.readyToExit) {
            return
          } else {
            /**
             * We need to reload the window after the user successfully
             * purchased a reviewer. This way, the updated userInfo will be 
             * fetched. They can then access CAAP 101 immediately after they
             * purchased.
             */
            // this.$emit("closePurchaseModal");
            this.handleExitPurchaseModal();
          }
        }else{
        this.isProcessingPayment = false;
          this.$emit("closePurchaseModal");
          setTimeout(() => {
            this.hasCoupon = false;
            this.slide_index = 0;
          }, 500);
        }
  
      },
      nextStep(index) {
        this.slide_index = index;
        document.querySelector("body").scrollTop;
      },
  
      async prepareToOnboard() {
      // Guard against duplicate calls - prevent multiple simultaneous transaction submissions
      if (this.isProcessingPayment) {
        console.warn("Payment processing already in progress. Ignoring duplicate call.");
        return;
      }

      // Set processing flag immediately to prevent duplicate calls
      this.isProcessingPayment = true;

      try {
        // let isValidEndorserCode = await userService.isValidEndorserCode(db, this.user.endorser)
        // if(!isValidEndorserCode){
        //   alert("Endorser code not found")
        // }else{
        // this.user.level = (this.isStudent) ? 'Undergraduate' : '';
        // console.log("user: ", this.user)

        if (this.didNotPayThruGcash) {
          await this.uploadPaymentImage();

          if (this.proofImageURL == null) {
            alert("Unable to upload the proof of payment image. Please try again later.")
            this.isProcessingPayment = false;
            return;
          }
        }

        this.nextStep(4) // navigate to the final slide
        this.onboardUser(); // run loaders and tips
        // }    
      } catch (error) {
        console.error("Error during payment processing:", error);
        this.isProcessingPayment = false;
        alert("An error occurred while processing your payment. Please try again.");
      }
      },
  
      async uploadPaymentImage() {
        try {
          this.proofImageURL = null;
  
          const filePath = `images/proofs/${this.user.id}-${Date.now()}-${this.paymentImage.name}`;
          const fileRef = storageRef(storage, filePath);
  
          await uploadBytes(fileRef, this.paymentImage);
          const downloadURL = await getDownloadURL(fileRef);
  
          // Attach to _caap_data during onboarding
          this.proofImageURL = downloadURL;
        } catch (err) {
          console.error("Error uploading image:", err);
          alert("Failed to upload the proof of payment image. Please try again.");
        }
      },
  
      /**
       * ENG-34: stamp user reviewer payloads with server time.
       * Prefer the committed transactions.purchase_date Timestamp. If that write
       * failed, still use serverTimestamp() for purchase_date_v2 so Home pending
       * buyers is not left on the device clock. The legacy string field cannot
       * store a sentinel, so it falls back to moment only on that failure path.
       */
      applyPurchaseTimestamps(payload, committedPurchaseDate) {
        const reviewerDataKeys = ['caap_data', 'caap_plus_data', 'mtp_data', 'caap_lite_data'];
        const hasCommittedTime = committedPurchaseDate && typeof committedPurchaseDate.toMillis === 'function';
        const purchaseDateV2 = hasCommittedTime
          ? committedPurchaseDate
          : serverTimestamp();
        const purchaseDateEpoch = hasCommittedTime
          ? String(committedPurchaseDate.toMillis())
          : moment().valueOf().toString();

        reviewerDataKeys.forEach((key) => {
          if (!payload[key]) return;
          payload[key].purchase_date_v2 = purchaseDateV2;
          payload[key].purchase_date = purchaseDateEpoch;
        });
      },

      /**
       * School id is already on the in-memory user; name needs one `schools` get.
       */
      async resolveSchoolForTransaction() {
        const schoolId = this.user?.school || null;
        if (!schoolId) {
          return { user_school: null, user_school_name: null };
        }
        try {
          const snap = await getDoc(doc(db, "schools", schoolId));
          const name = snap.exists() ? (snap.data().name || null) : null;
          return { user_school: schoolId, user_school_name: name };
        } catch (error) {
          console.warn("Failed to resolve school name for transaction", error);
          return { user_school: schoolId, user_school_name: null };
        }
      },

      async resolveEndorserNameForTransaction() {
        if (!this.hasValidCoupon || !this.coupon?.data?.assignedToId) {
          return null;
        }
        try {
          const snap = await getDoc(doc(db, "endorsers", this.coupon.data.assignedToId));
          if (!snap.exists()) return null;
          const data = snap.data() || {};
          const name = [data.firstName, data.lastName].filter(Boolean).join(" ").trim();
          return name || null;
        } catch (error) {
          console.warn("Failed to resolve endorser name for transaction", error);
          return null;
        }
      },

      /**
       * Creates a transaction record in the "transactions" collection
       * @param {Object} payload - The payload object containing reviewer data
       * @param {Number} originalPrice - The original price before discounts
       * @param {Number} finalPrice - The final price after discounts
       * @param {Number} duration - Duration in days
       * @param {String} durationInWords - Duration in words (e.g., "3 months")
       * @returns {Promise<import('firebase/firestore').Timestamp|null>} Server purchase_date, or null if the write failed
       */
      async createTransactionRecord(payload, originalPrice, finalPrice, duration, durationInWords) {
        try {
          // Determine purchase type
          const isBundled = this.selectedPricing.key === 'full-license-bundle' || 
                           this.selectedPricing.key === 'full-airman-bundle';
          const purchaseType = isBundled ? 'bundled' : 'individual';
  
          // Build subscriptions array
          const subscriptions = [];
  
          // Individual reviewer standard prices (for bundles, to calculate partial_amount)
          // These are the standard 3-month prices
          const STANDARD_INDIVIDUAL_PRICES = {
            'caap-knowledge': 3999,
            'caap-practical': 3999,
            'entrance-exam': 3999,
          };
  
          if (this.selectedPricing.key === 'caap-knowledge') {
            // Individual purchase - use the actual originalPrice
            subscriptions.push({
              reviewer_id: 'caap-knowledge',
              reviewer_name: 'CAAP Knowledge',
              duration_in_words: durationInWords,
              duration_in_days: duration,
              original_price: originalPrice,
            });
          } else if (this.selectedPricing.key === 'caap-practical') {
            // Individual purchase - use the actual originalPrice
            subscriptions.push({
              reviewer_id: 'caap-practical',
              reviewer_name: 'CAAP Practical',
              duration_in_words: durationInWords,
              duration_in_days: duration,
              original_price: originalPrice,
            });
          } else if (this.selectedPricing.key === 'entrance-exam') {
            // Individual purchase - use the actual originalPrice
            subscriptions.push({
              reviewer_id: 'entrance-exam',
              reviewer_name: 'MTP Entrance',
              duration_in_words: durationInWords,
              duration_in_days: duration,
              original_price: originalPrice,
            });
          } else if (this.selectedPricing.key === 'full-license-bundle') {
            // Bundle includes CAAP Knowledge and CAAP Practical
            // Use standard individual prices for each reviewer
            subscriptions.push({
              reviewer_id: 'caap-knowledge',
              reviewer_name: 'CAAP Knowledge',
              duration_in_words: durationInWords,
              duration_in_days: duration,
              original_price: STANDARD_INDIVIDUAL_PRICES['caap-knowledge'],
            });
            subscriptions.push({
              reviewer_id: 'caap-practical',
              reviewer_name: 'CAAP Practical',
              duration_in_words: durationInWords,
              duration_in_days: duration,
              original_price: STANDARD_INDIVIDUAL_PRICES['caap-practical'],
            });
          } else if (this.selectedPricing.key === 'full-airman-bundle') {
            // Bundle includes CAAP Knowledge, CAAP Practical, and MTP Entrance
            // Use standard individual prices for each reviewer
            subscriptions.push({
              reviewer_id: 'caap-knowledge',
              reviewer_name: 'CAAP Knowledge',
              duration_in_words: durationInWords,
              duration_in_days: duration,
              original_price: STANDARD_INDIVIDUAL_PRICES['caap-knowledge'],
            });
            subscriptions.push({
              reviewer_id: 'caap-practical',
              reviewer_name: 'CAAP Practical',
              duration_in_words: durationInWords,
              duration_in_days: duration,
              original_price: STANDARD_INDIVIDUAL_PRICES['caap-practical'],
            });
            subscriptions.push({
              reviewer_id: 'entrance-exam',
              reviewer_name: 'MTP Entrance',
              duration_in_words: durationInWords,
              duration_in_days: duration,
              original_price: STANDARD_INDIVIDUAL_PRICES['entrance-exam'],
            });
          }
  
          // Calculate partial amount (sum of all individual prices)
          // For individual purchases, this equals originalPrice
          // For bundles, this is the sum of standard individual prices
          const partialAmount = subscriptions.reduce((sum, sub) => sum + sub.original_price, 0);
  
          // Calculate bundle discount
          // For bundles: bundle_discount = partialAmount - originalPrice (bundle price)
          // For individual purchases: bundle_discount = 0
          const bundleDiscount = isBundled 
            ? partialAmount - originalPrice 
            : 0;
  
          // Extract coupon data
          let coupon = null;
        const couponDiscount = this.hasValidCoupon
          ? this.coupon.data.discountAmount
          : 0;

        if (this.hasValidCoupon) {
          coupon = {
            coupon_code: this.coupon.data.code,
            coupon_discount: couponDiscount,
            coupon_endorser_code: this.coupon.data.assignedToEndorserCode || null,
            coupon_endorser_name: await this.resolveEndorserNameForTransaction(),
          };
        }
  
          // Calculate total amount: partial_amount - bundle_discount - coupon_discount
          // This should equal finalPrice (what the user actually paid)
          const totalAmount = partialAmount - bundleDiscount - couponDiscount;
  
          // Get bundle name (null for individual purchases)
          const bundleName = isBundled ? this.selectedPurchase : null;
  
          // Check if this is an admin purchase (admin purchasing for another user)
          const currentAdminId = auth.currentUser?.uid;
          const isPurchasedByAdmin = currentAdminId && currentAdminId !== this.user.id;
          const purchasedByAdminId = isPurchasedByAdmin ? currentAdminId : null;
          const purchasedByAdminEmail = isPurchasedByAdmin ? auth.currentUser?.email || null : null;

          const schoolFields = await this.resolveSchoolForTransaction();

          // Build transaction object
          const transaction = {
            purchase_type: purchaseType,
            bundle_name: bundleName,
            subscriptions: subscriptions,
            coupon: coupon,
            gcash_ref_code: this.refno || null,
            proof_image_url: this.proofImageURL || null,
            qr: this.buildTransactionQr(),
            partial_amount: partialAmount,
            bundle_discount: bundleDiscount,
            coupon_discount: couponDiscount,
            total_amount: totalAmount,
            user_id: this.user.id,
            user_email: this.user.email || null,
            user_firstname: this.user.firstname || null,
            user_lastname: this.user.lastname || null,
            user_phone: this.user.phone || null,
            user_school: schoolFields.user_school,
            user_school_name: schoolFields.user_school_name,
            isPurchasedByAdmin: isPurchasedByAdmin,
            purchasedByAdminId: purchasedByAdminId,
            purchasedByAdminEmail: purchasedByAdminEmail,
            purchase_date: serverTimestamp(),
            confirmation_date: null,
            confirmed_by: null,
            revocation_date: null,
            revoked_by: null,
            status: 'pending', // pending, confirmed, or revoked
          };
  
          // Create transaction document in Firestore
          const docRef = await addDoc(collection(db, "transactions"), transaction);
          const snap = await getDoc(docRef);
          const committedPurchaseDate = snap.exists() ? snap.data().purchase_date : null;

          console.log("✅ Transaction record created successfully", docRef.id);

          return committedPurchaseDate || null;
  
        } catch (error) {
          console.error("❌ Error creating transaction record:", error);
          // Don't throw error - we still want to proceed with user update even if transaction recording fails
          return null;
        }
      },
  
      async onboardUser() {
  
        var current_progress = 0;
        var step = 0.05; // the smaller this is the slower the progress bar
  
        console.log("Processing payment...")
  
        // run interval for progress bar
        const interval = setInterval(() => {
          current_progress += step;
          this.progress = Math.round(Math.atan(current_progress) / (Math.PI / 2) * 100 * 1000) / 1000
          if (this.progress >= 100) {
            clearInterval(interval);
          } else if (this.progress >= 2) {
            step = 1.0
          }
        }, 100);
  
        // run interval for tips
        const tipsInterval = setInterval(() => {
          this.current = (this.current < this.tips.length - 1) ? this.current + 1 : 0;
          console.log(this.current)
        }, 4000);
  
  
  
        var _reviewers = { ...this.user.reviewers };
  
        /**
         * identify the reviewer being purchased
         */
        console.log("%c================================================", "color: aqua;")
        console.log("%cThe data being used: ", "color: aqua;")
        console.log(this.user)
        console.log(this.selectedPurchase)
        console.log(this.selectedPricing)
        console.log("%c================================================", "color: aqua;")
  
        var ORIGINAL_PRICE = this.selectedPricing.selectedDuration.price;
        if (this.hasValidCoupon) {
          var FINAL_PRICE = ORIGINAL_PRICE - this.coupon.data.discountAmount;
        } else {
          var FINAL_PRICE = ORIGINAL_PRICE;
        }
        var DURATION = this.selectedPricing.selectedDuration.duration;
        var DURATION_IN_WORDS = this.selectedPricing.selectedDuration.durationInWords;
        var payload = {};
  
        if (this.selectedPricing.key == 'caap-knowledge') {
  
          _reviewers.caap = 'paid';
  
          /** IMPORTANT: purchase_date is a legacy moment epoch string.
           * purchase_date_v2 is a Firestore Timestamp. Both are overwritten
           * after the transaction write with server time (ENG-34). */
  
          let _caap_data = {
            purchase_date_v2: new Date(),
            purchase_date: moment().valueOf().toString(),
            gcash_refno: this.refno,
            proof_image_url: this.proofImageURL,
          }
  
          // attach all the coupon data if the user purchased with coupon
        if (this.hasValidCoupon) {
            _caap_data.couponId = this.coupon.id;
            _caap_data.couponCode = this.coupon.data.code;
            _caap_data.couponDiscount = this.coupon.data.discountAmount;
            _caap_data.endorserCode = this.coupon.data.assignedToEndorserCode || null;
  
            _caap_data.originalPrice = ORIGINAL_PRICE;
            _caap_data.finalPrice = FINAL_PRICE;
            _caap_data.activateLater = true;
            _caap_data.isBundled = false;
            _caap_data.bundleType = ''; // no bundle
            _caap_data.duration = DURATION;
            _caap_data.durationInWords = DURATION_IN_WORDS;
          } else {
            _caap_data.originalPrice = ORIGINAL_PRICE;
            _caap_data.finalPrice = FINAL_PRICE;
            _caap_data.activateLater = true;
            _caap_data.isBundled = false;
            _caap_data.bundleType = ''; // no bundle
            _caap_data.duration = DURATION;
            _caap_data.durationInWords = DURATION_IN_WORDS;
          }
  
          // set the final payload
          payload = {
            reviewers: _reviewers,
            caap_data: _caap_data
          }
  
        } else if (this.selectedPricing.key == 'caap-practical') {
  
          _reviewers.caap_plus = 'paid';
  
          let _plus_data = {
            purchase_date_v2: new Date(),
            purchase_date: moment().valueOf().toString(),
            // purchase_date: new Date(),
            gcash_refno: this.refno,
            proof_image_url: this.proofImageURL,
          }
  
          // attach all the coupon data if the user purchased with coupon
        if (this.hasValidCoupon) {
            _plus_data.couponId = this.coupon.id;
            _plus_data.couponCode = this.coupon.data.code;
            _plus_data.couponDiscount = this.coupon.data.discountAmount;
            _plus_data.endorserCode = this.coupon.data.assignedToEndorserCode || null;
  
            _plus_data.originalPrice = ORIGINAL_PRICE;
            _plus_data.finalPrice = FINAL_PRICE;
            _plus_data.activateLater = true;
            _plus_data.isBundled = false;
            _plus_data.bundleType = ''; // no bundle
            _plus_data.duration = DURATION;
            _plus_data.durationInWords = DURATION_IN_WORDS;
          } else {
            _plus_data.originalPrice = ORIGINAL_PRICE;
            _plus_data.finalPrice = FINAL_PRICE;
            _plus_data.activateLater = true;
            _plus_data.isBundled = false;
            _plus_data.bundleType = ''; // no bundle
            _plus_data.duration = DURATION;
            _plus_data.durationInWords = DURATION_IN_WORDS;
          }
  
          // set the final payload
          payload = {
            reviewers: _reviewers,
            caap_plus_data: _plus_data,
          }
  
        } else if (this.selectedPricing.key == 'entrance-exam') {
          _reviewers.mtp = 'paid';
  
          let _mtp_data = {
            purchase_date_v2: new Date(),
            purchase_date: moment().valueOf().toString(),
            // purchase_date: new Date(),
            gcash_refno: this.refno,
            proof_image_url: this.proofImageURL,
          }
  
          // attach all the coupon data if the user purchased with coupon
        if (this.hasValidCoupon) {
            _mtp_data.couponId = this.coupon.id;
            _mtp_data.couponCode = this.coupon.data.code;
            _mtp_data.couponDiscount = this.coupon.data.discountAmount;
            _mtp_data.endorserCode = this.coupon.data.assignedToEndorserCode || null;
  
            _mtp_data.originalPrice = ORIGINAL_PRICE;
            _mtp_data.finalPrice = FINAL_PRICE;
            _mtp_data.activateLater = true;
            _mtp_data.isBundled = false;
            _mtp_data.bundleType = ''; // no bundle
            _mtp_data.duration = DURATION;
            _mtp_data.durationInWords = DURATION_IN_WORDS;
          } else {
            _mtp_data.originalPrice = ORIGINAL_PRICE;
            _mtp_data.finalPrice = FINAL_PRICE;
            _mtp_data.activateLater = true;
            _mtp_data.isBundled = false;
            _mtp_data.bundleType = ''; // no bundle
            _mtp_data.duration = DURATION;
            _mtp_data.durationInWords = DURATION_IN_WORDS;
          }
  
          // set the final payload
          payload = {
            reviewers: _reviewers,
            mtp_data: _mtp_data,
          }
  
        } else if (this.selectedPricing.key == 'full-license-bundle') {
          _reviewers.caap = 'paid';
  
          let _caap_data = {
            purchase_date_v2: new Date(),
            purchase_date: moment().valueOf().toString(),
            // purchase_date: new Date(),
            gcash_refno: this.refno,
            proof_image_url: this.proofImageURL,
          }
  
          // attach all the coupon data if the user purchased with coupon
        if (this.hasValidCoupon) {
            _caap_data.couponId = this.coupon.id;
            _caap_data.couponCode = this.coupon.data.code;
            _caap_data.couponDiscount = this.coupon.data.discountAmount;
            _caap_data.endorserCode = this.coupon.data.assignedToEndorserCode || null;
  
            _caap_data.originalPrice = ORIGINAL_PRICE;
            _caap_data.finalPrice = FINAL_PRICE;
            _caap_data.activateLater = true;
            _caap_data.isBundled = true;
            _caap_data.bundleType = this.selectedPricing.key;
            _caap_data.duration = DURATION;
            _caap_data.durationInWords = DURATION_IN_WORDS;
          } else {
            _caap_data.originalPrice = ORIGINAL_PRICE;
            _caap_data.finalPrice = FINAL_PRICE;
            _caap_data.activateLater = true;
            _caap_data.isBundled = true;
            _caap_data.bundleType = this.selectedPricing.key;
            _caap_data.duration = DURATION;
            _caap_data.durationInWords = DURATION_IN_WORDS;
          }
  
          _reviewers.caap_plus = 'paid';
  
          let _plus_data = {
            purchase_date_v2: new Date(),
            purchase_date: moment().valueOf().toString(),
            // purchase_date: new Date(),
            gcash_refno: this.refno,
            proof_image_url: this.proofImageURL,
          }
  
          // attach all the coupon data if the user purchased with coupon
        if (this.hasValidCoupon) {
            _plus_data.couponId = this.coupon.id;
            _plus_data.couponCode = this.coupon.data.code;
            _plus_data.couponDiscount = this.coupon.data.discountAmount;
            _plus_data.endorserCode = this.coupon.data.assignedToEndorserCode || null;
  
            _plus_data.originalPrice = ORIGINAL_PRICE;
            _plus_data.finalPrice = FINAL_PRICE;
            _plus_data.activateLater = true;
            _plus_data.isBundled = true;
            _plus_data.bundleType = this.selectedPricing.key;
            _plus_data.duration = DURATION;
            _plus_data.durationInWords = DURATION_IN_WORDS;
          } else {
            _plus_data.originalPrice = ORIGINAL_PRICE;
            _plus_data.finalPrice = FINAL_PRICE;
            _plus_data.activateLater = true;
            _plus_data.isBundled = true;
            _plus_data.bundleType = this.selectedPricing.key;
            _plus_data.duration = DURATION;
            _plus_data.durationInWords = DURATION_IN_WORDS;
          }
  
          // set the final payload
          payload = {
            reviewers: _reviewers,
            caap_data: _caap_data,
            caap_plus_data: _plus_data,
          }
  
        } else if (this.selectedPricing.key == 'full-airman-bundle') {
          _reviewers.caap = 'paid';
  
          let _caap_data = {
            purchase_date_v2: new Date(),
            purchase_date: moment().valueOf().toString(),
            // purchase_date: new Date(),
            gcash_refno: this.refno,
            proof_image_url: this.proofImageURL,
          }
  
          // attach all the coupon data if the user purchased with coupon
        if (this.hasValidCoupon) {
            _caap_data.couponId = this.coupon.id;
            _caap_data.couponCode = this.coupon.data.code;
            _caap_data.couponDiscount = this.coupon.data.discountAmount;
            _caap_data.endorserCode = this.coupon.data.assignedToEndorserCode || null;
  
            _caap_data.originalPrice = ORIGINAL_PRICE;
            _caap_data.finalPrice = FINAL_PRICE;
            _caap_data.activateLater = true;
            _caap_data.isBundled = true;
            _caap_data.bundleType = this.selectedPricing.key;
            _caap_data.duration = DURATION;
            _caap_data.durationInWords = DURATION_IN_WORDS;
          } else {
            _caap_data.originalPrice = ORIGINAL_PRICE;
            _caap_data.finalPrice = FINAL_PRICE;
            _caap_data.activateLater = true;
            _caap_data.isBundled = true;
            _caap_data.bundleType = this.selectedPricing.key;
            _caap_data.duration = DURATION;
            _caap_data.durationInWords = DURATION_IN_WORDS;
          }
  
          _reviewers.caap_plus = 'paid';
  
          let _plus_data = {
            purchase_date_v2: new Date(),
            purchase_date: moment().valueOf().toString(),
            // purchase_date: new Date(),
            gcash_refno: this.refno,
            proof_image_url: this.proofImageURL,
          }
  
          // attach all the coupon data if the user purchased with coupon
        if (this.hasValidCoupon) {
            _plus_data.couponId = this.coupon.id;
            _plus_data.couponCode = this.coupon.data.code;
            _plus_data.couponDiscount = this.coupon.data.discountAmount;
            _plus_data.endorserCode = this.coupon.data.assignedToEndorserCode || null;
  
            _plus_data.originalPrice = ORIGINAL_PRICE;
            _plus_data.finalPrice = FINAL_PRICE;
            _plus_data.activateLater = true;
            _plus_data.isBundled = true;
            _plus_data.bundleType = this.selectedPricing.key;
            _plus_data.duration = DURATION;
            _plus_data.durationInWords = DURATION_IN_WORDS;
          } else {
            _plus_data.originalPrice = ORIGINAL_PRICE;
            _plus_data.finalPrice = FINAL_PRICE;
            _plus_data.activateLater = true;
            _plus_data.isBundled = true;
            _plus_data.bundleType = this.selectedPricing.key;
            _plus_data.duration = DURATION;
            _plus_data.durationInWords = DURATION_IN_WORDS;
          }
  
          _reviewers.mtp = 'paid';
  
          let _mtp_data = {
            purchase_date_v2: new Date(),
            purchase_date: moment().valueOf().toString(),
            // purchase_date: new Date(),
            gcash_refno: this.refno,
            proof_image_url: this.proofImageURL,
          }
  
          // attach all the coupon data if the user purchased with coupon
        if (this.hasValidCoupon) {
            _mtp_data.couponId = this.coupon.id;
            _mtp_data.couponCode = this.coupon.data.code;
            _mtp_data.couponDiscount = this.coupon.data.discountAmount;
            _mtp_data.endorserCode = this.coupon.data.assignedToEndorserCode || null;
  
            _mtp_data.originalPrice = ORIGINAL_PRICE;
            _mtp_data.finalPrice = FINAL_PRICE;
            _mtp_data.activateLater = true;
            _mtp_data.isBundled = true;
            _mtp_data.bundleType = this.selectedPricing.key;
            _mtp_data.duration = DURATION;
            _mtp_data.durationInWords = DURATION_IN_WORDS;
          } else {
            _mtp_data.originalPrice = ORIGINAL_PRICE;
            _mtp_data.finalPrice = FINAL_PRICE;
            _mtp_data.activateLater = true;
            _mtp_data.isBundled = true;
            _mtp_data.bundleType = this.selectedPricing.key;
            _mtp_data.duration = DURATION;
            _mtp_data.durationInWords = DURATION_IN_WORDS;
          }
  
          // set the final payload
          payload = {
            reviewers: _reviewers,
            caap_data: _caap_data,
            caap_plus_data: _plus_data,
            mtp_data: _mtp_data,
          }
  
        } else {
          console.error("Invalid reviewer selected: ", this.selectedPurchase.name)
          return;
        }
  
        console.log("%c================================================", "color: gold;")
        console.log("%cpayload: ", "color: gold;", payload)
        console.log("%c================================================", "color: gold;")
  
        setTimeout(() => {
          clearInterval(tipsInterval);
            clearInterval(interval);
          this.readyToExit = true;
        }, 3000);
  
        /**
         * NEW: November 29, 2025
         * Create a new transaction object in the "transactions" collection.
         * This will be used to track all purchases and refunds.
         */
        const committedPurchaseDate = await this.createTransactionRecord(payload, ORIGINAL_PRICE, FINAL_PRICE, DURATION, DURATION_IN_WORDS);
        this.applyPurchaseTimestamps(payload, committedPurchaseDate);

        // Update User Data
        updateDoc(doc(db, "users", this.user.id), payload)
          .then(async () => {
            console.log("Successfully updated user")
  
            // update coupon usage here.
          if (this.hasValidCoupon) {
              await useCoupon(this.coupon, this.user)
            }
  
            // Track completed purchase
            const pricingKey = this.selectedPricing.key
            const purchaseName = this.selectedPurchase
            const originalPrice = ORIGINAL_PRICE
            const finalPrice = FINAL_PRICE
            const duration = DURATION
            const hasCoupon = this.hasValidCoupon
            const couponCode = hasCoupon ? this.coupon.data.code : null
            const couponDiscount = hasCoupon ? this.coupon.data.discountAmount : 0
            const isBundled = pricingKey === 'full-license-bundle' || pricingKey === 'full-airman-bundle'
            
            trackPurchaseCompleted(
              purchaseName,
              pricingKey,
              originalPrice,
              finalPrice,
              duration,
              hasCoupon,
              couponCode,
              couponDiscount,
              isBundled
            )
  
            clearInterval(tipsInterval);
            clearInterval(interval);
  
            // enable buttons and everything
            this.progress = 100; // set progress to 100
  
            setTimeout(() => {
              // add 1 second before removing the loader
              this.readyToExit = true;
            }, 1000)
  
          })
          .catch((error) => {
            console.error("Error updating user data: ", error);
          });
  
      },
  
    },
  };
  </script>